import React, { memo, useMemo } from "react";
import { motion } from "framer-motion";
import { EditableText } from "../../../common/EditableText";
import { LucideIcon } from "lucide-react";
import { getIconComponent } from "../utils/icons";
import { GoalItem } from "../types";
import { sceneItemClassName, useCardSceneLayout } from "../../../../presentation/cardSceneLayout";

export const GoalCard = memo(({ goal, index, isEditMode }: {
    goal: GoalItem;
    index: number;
    isEditMode: boolean;
}) => {
    const Icon = useMemo<LucideIcon>(() => getIconComponent(goal.iconName), [goal.iconName]);
    const layout = useCardSceneLayout();

    return (
        <article
            className={sceneItemClassName(`relative p-4 sm:p-5 ${isEditMode ? 'glass-border-2-no-overflow' : 'glass-border-2'} transition-all duration-500 ease-out group hover:scale-[1.03] cursor-pointer`, layout)}
            aria-labelledby={`goal-title-${index}`}
            aria-describedby={`goal-description-${index}`}
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); } }}
        >
            <div className="relative z-10">
                <header className={layout.isCard ? "flex items-center mb-1.5" : "flex items-center mb-3"}>
                    <div className={`p-2 sm:p-2.5 ${isEditMode ? 'glass-border-4-no-overflow' : 'glass-border-4'} rounded-xl ltr:mr-3 transition-all duration-300 ease-out group-hover:scale-105 rtl:ml-3`} aria-hidden="true">
                        <Icon size={16} className={'relative z-10 transition-all duration-300 ease-out group-hover:scale-105 text-[#1C1C1E] dark:text-[#F2F2F7]'} aria-hidden="true" />
                    </div>
                    <div className="flex flex-col items-start">
                        <motion.h3 id={`goal-title-${index}`} className={`${layout.isCard ? "text-[1.05rem]" : "text-base"} font-semibold text-[#1C1C1E] dark:text-[#F2F2F7] transition-colors duration-300 ease-out`}>
                            <EditableText configPath={`goals.${index}.title`} placeholder="Enter goal title..." maxLength={100} as="span">
                                {goal.title}
                            </EditableText>
                        </motion.h3>
                    </div>
                </header>
                <div id={`goal-description-${index}`} className={`${layout.isCard ? "text-[0.95rem] leading-normal" : "text-sm leading-relaxed"} text-left text-[#1C1C1E] dark:text-[#F2F2F7] transition-colors duration-300 ease-out rtl:text-right`}>
                    <EditableText configPath={`goals.${index}.description`} placeholder="Enter goal description..." maxLength={300} multiline={true} as="span">
                        {goal.description}
                    </EditableText>
                </div>
            </div>
        </article>
    );
});

GoalCard.displayName = 'GoalCard';


